import React, { memo, useEffect, useState } from "react";
import moment, { LocaleSpecifier } from "moment";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { capitalize } from "../../utils";
import UserGroup from "../../assets/images/user-group";
import DateRangeIcon from "@material-ui/icons/DateRange";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { selectionStore } from "../../stores/selection.store";
import { InputWithIcon } from "../input-with-icon/input-with-icon";
import { ActionButton } from "../action-button/action-button";
import selectionGuestsService from "../../services/selection-guests-service";
import { pendingBookingStore } from "../../stores/pending-booking.store";
import commercialsService from "../../services/commercials.service";
import { useLocation } from "react-router-dom";
import { getLanguageSwitcherService } from "../../services/language-switcher.service";

const SummaryCriteriaWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  padding-bottom: 32px;
  width: 100%;
  justify-content: end;
`;

const BackButton = styled.div`
  position: absolute;
  margin-left: -36px;
  left: 0;
  display: flex;
  align-items: center;

  @media screen and (max-width: 930px) {
    margin-left: 0;
    left: 0;
  }

  #back svg {
    transform: translateX(4px);
  }
`;

const Inputs = styled.div`
  display: flex;
  width: 100%;

  .input-with-icon {
    padding-right: 6px;
  }

  @media screen and (max-width: 930px) {
    width: calc(100% - 32px - 4px);
    margin-left: 36px;

    .input-with-icon:nth-child(1) {
      width: 25%;
    }

    .input-with-icon:nth-child(2) {
      width: 50%;
    }

    .input-with-icon:nth-child(3) {
      width: 30%;
      padding-right: 0;
    }

    .input-with-icon input {
      text-align: center;
      position: relative;
      left: 50%;
      transform: translateX(-50%);
      margin-right: -3px;
    }

    .input-with-icon svg {
      display: none;
    }
  }
`;

const SummaryCriteriaLayout = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const selectedGuests = selectionStore.guests + selectionStore.children;
  const selectedDate = selectionStore.date;
  const selectedTime = selectionStore.time;
  const hasCommercialPage =
    pendingBookingStore.getIsVoucherEnabled() ||
    commercialsService.hasCommercials();
  const [localeForMoment, setLocaleForMoment] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(0);

  const handleBackButton = () => {
    selectionStore.backButtonClicked = true;
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLocaleForMoment(getLanguageSwitcherService().localeForMoment());
  }, [i18n.language]);

  const setBackRoute =
    location.pathname.endsWith("confirmation") && hasCommercialPage
      ? "commercials"
      : "selection";

  const setCancelPB =
    location.pathname.endsWith("confirmation") && hasCommercialPage
      ? false
      : true;

  const selectedGuestsValue =
    windowWidth > 550
      ? selectionGuestsService.setSelectedGuestsToString(selectedGuests)
      : selectedGuests;

  const format = windowWidth > 550 ? "dddd LL" : "L";

  const selectedDateValue =
    Number(selectedDate) > 0 && !!localeForMoment
      ? capitalize(moment(selectedDate).locale(localeForMoment).format(format))
      : "";

  return (
    <SummaryCriteriaWrapper data-test="summary-criteria">
      <BackButton>
        <ActionButton
          route={setBackRoute}
          cancelPB={setCancelPB}
          action={"back"}
          handleClick={handleBackButton}
        />
      </BackButton>
      <Inputs>
        <InputWithIcon
          error={false}
          value={selectedGuestsValue}
          helperText={""}
          variant={"outlined"}
          icon={<UserGroup />}
          isSummary={true}
          dataTest="selected-pax"
        />
        <InputWithIcon
          error={false}
          value={selectedDateValue}
          helperText={""}
          variant={"outlined"}
          icon={<DateRangeIcon />}
          isSummary={true}
          dataTest="selected-date"
        />
        <InputWithIcon
          error={false}
          value={selectedTime}
          helperText={""}
          variant={"outlined"}
          icon={<AccessTimeIcon />}
          isSummary={true}
          dataTest="selected-time"
        />
      </Inputs>
    </SummaryCriteriaWrapper>
  );
};
export const SummaryCriteria = memo(SummaryCriteriaLayout);
